import { yupResolver } from '@hookform/resolvers/yup';
import {
  CreatePatientDto,
  DoctorType,
  PatientDto,
  QueryFactory,
  SubtenantDto,
} from '@lmt/shared-types';

import {
  CustomDictionaryName,
  useActiveDictionaryValuesByTitle,
} from '@lmt/unicorn-shared/app/api/custom-hooks';

import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { SubmitButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/SubmitButton';
import { CheckBox } from '@lmt/unicorn-shared/app/components/common/ui-kit/CheckBox';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { UpdateContactWhenFieldsChange } from '@lmt/unicorn-shared/app/components/patients/patient-form/common/UpdateContactsOnFieldsChange';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { useTabs } from '@lmt/unicorn-shared/app/features/navigation/operations';
import { SettingsActions } from '@lmt/unicorn-shared/app/features/settings/reducer';
import { getSettings } from '@lmt/unicorn-shared/app/features/settings/selectors';
import { useSubtenants } from '@lmt/unicorn-shared/app/features/subtenants/selectors';
import { emptyArray } from '@lmt/unicorn-shared/app/helpers/common';
import { validateAndDirty } from '@lmt/unicorn-shared/app/helpers/form-helper';
import {
  handleSubmitFormError,
  useErrorHandler,
} from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import {
  useUniDispatch,
  useUniSelector,
} from '@lmt/unicorn-shared/app/store/types';
import { useEventCallback } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { useDebounce } from 'use-debounce';
import { FormComponentProps } from '../../global-forms/GlobalForms.types';
import { useGlobalForms } from '../../global-forms/useGlobalForms';
import { patientDataYupSchema, titleKey } from './common/common';
import { PatientData } from './common/types';
import { ExpandedPatientForm } from './components/ExpandedPatientForm';
import { PatientMiniForm } from './components/PatientMiniForm';

import styles from './CreatePatient.module.scss';

type CreatePatientFormProps = FormComponentProps & {
  selectedSubtenantId?: number;
  selectedDoctorId?: number;
  defaultValues?: Partial<PatientData>;
};

export const CreatePatient: FC<CreatePatientFormProps> = (props) => {
  const {
    formType,
    formId,
    tryCompleteForm,
    isExpanded,
    selectedSubtenantId,
    selectedDoctorId,
    registerFormDirtyStateRetriever,
    defaultValues,
  } = props;

  const queryClient = useQueryClient();
  const i18next = useScopedTranslation('patients.form.create_patient');
  const globalForms = useGlobalForms();
  const tabs = useTabs();
  const dispatch = useUniDispatch();
  const modals = useModal();
  const isOpenPatientProfile =
    useUniSelector(getSettings).isOpenPatientPageAfterCreation;
  const subtenants: SubtenantDto[] = useSubtenants();
  const isVisibleSubtenants = subtenants.length > 1;
  const [isCustomFieldsValid, setIsCustomFieldsValid] = useState(true);
  const [customFieldsDirty, setCustomFieldsDirty] = useState(false);
  const [isCustomFieldsErrorsShown, setCustomFieldErrorsShown] =
    useState(false);

  const [doctorsSearchQuery, setDoctorsSearchQuery] = useState<string | null>(
    null,
  );
  const [searchQueryDebounced] = useDebounce(doctorsSearchQuery, 100);

  const doctorsQuery = QueryFactory.DoctorQuery.useGetDoctorsQuery({
    doctorType: DoctorType.External,
    searchQuery: searchQueryDebounced,
    all: true,
    isOnlyActive: true,
  });
  const doctors = doctorsQuery.data?.data ?? emptyArray();

  const { data: personTitles } = useActiveDictionaryValuesByTitle(
    CustomDictionaryName.PersonTitles,
    defaultValues?.values?.[titleKey],
  );

  const schema = useMemo(patientDataYupSchema, []);
  const createForm = () => {
    return useForm<PatientData>({
      resolver: yupResolver(schema),
      shouldUnregister: false,
      // for some reason we need to set default values for <Controller> components
      defaultValues: {
        firstName: defaultValues?.firstName ?? '',
        lastName: defaultValues?.lastName ?? '',
        number: defaultValues?.number,
        sex: defaultValues?.sex ?? null!,
        subtenantId: selectedSubtenantId
          ? selectedSubtenantId
          : isVisibleSubtenants
            ? null!
            : subtenants[0].id,
        address: defaultValues?.address ?? '',
        location: defaultValues?.location ?? '',
        postcode: defaultValues?.postcode ?? '',
        dateOfBirth: defaultValues?.dateOfBirth ?? null!,
        insuranceNumber: defaultValues?.insuranceNumber ?? '',
        healthInsuranceCompanyId:
          defaultValues?.healthInsuranceCompanyId ?? null!,
        dateOfDeath: null!,
        doctorId: selectedDoctorId ? selectedDoctorId : null!,
        values: defaultValues?.values ?? {},
        emailsAllowed: defaultValues?.emailsAllowed ?? false,
      },
    });
  };

  const expandedForm = createForm();
  const miniForm = createForm();

  const { dirtyFields: expandedFormDirtyFields } = expandedForm.formState;
  const { dirtyFields: miniFormDirtyFields } = miniForm.formState;
  useEffect(() => {
    registerFormDirtyStateRetriever(
      formId,
      () =>
        Object.keys(miniFormDirtyFields).length > 0 ||
        Object.keys(expandedFormDirtyFields).length > 0 ||
        customFieldsDirty,
    );
  }, [expandedFormDirtyFields, miniFormDirtyFields, customFieldsDirty]);

  const form = isExpanded ? expandedForm : miniForm;
  const { handleSubmit, formState, setError } = form;

  const openPatientProfile = useEventCallback((patient: PatientDto) => {
    tabs.openTab(pages.patientProfile.link({ patientId: patient.id }));
  });

  const ChangeOpenPatientProfileSettings = useEventCallback((isOpen: boolean) =>
    dispatch(SettingsActions.isOpenPatientPageAfterCreationChanged(isOpen)),
  );

  const titleDictionaryItem = useMemo(() => {
    const titleId = defaultValues?.values?.[titleKey];
    if (!titleId || Number.isNaN(Number(titleId))) {
      return null;
    }
    return personTitles.find((x) => x.id === titleId);
  }, [defaultValues, personTitles]);

  // to save all changes in UIEditor
  const beforeSaveRef = useRef<() => Promise<void>>(null);
  const submitForm = useErrorHandler(
    useEventCallback(async (data: PatientData) => {
      if (!isCustomFieldsValid && isExpanded) {
        return;
      }
      try {
        await tryCompleteForm(
          formType,
          formId,
          async () => {
            // -----------------
            // When changing this code make sure to change the similar one in EditPatient.tsx!!!
            // -----------------
            if (beforeSaveRef.current) {
              data.values = await beforeSaveRef.current();
            }

            // data.customFields only contains CHANGED fields. So we need to apply them above other predefined customFields
            const customValues = {
              ...data?.values,
              'Health insurance company': data.healthInsuranceCompanyId,
              'Family doctor': data.doctorId,
            };
            if (titleDictionaryItem?.isDeleted) {
              // we can't save patient with deleted dictionary item, so we replace it with string value
              customValues[titleKey] = titleDictionaryItem.value;
            }
            const patient: CreatePatientDto = {
              firstName: data.firstName.trim(),
              lastName: data.lastName.trim(),
              dateOfBirth: data.dateOfBirth!,
              sex: data.sex,
              subtenantId: data.subtenantId,
              mobilePhone: data.mobilePhone,
              email: data.email,
              emailsAllowed: data.emailsAllowed,
              doctorId: data.doctorId,
              address: data.address,
              location: data.location,
              postcode: data.postcode,
              homePhone: data.homePhone,
              workPhone: data.workPhone,
              fax: data.fax,
              number: data.number,
              healthInsuranceCompanyId: data.healthInsuranceCompanyId,
              isPrivateInsurance: data.isPrivateInsurance,
              isSelfPayer: data.isSelfPayer,
              insuranceNumber: data.insuranceNumber,
              values: customValues,
              priorityContactType: data.priorityContactType,
              comments: data.comments,
            };
            const createdPatient =
              await QueryFactory.PatientClient.createPatient(patient);
            await queryClient.invalidateQueries({
              queryKey: QueryFactory.PatientDuplicateQuery.getQueryKey(),
            });
            if (isOpenPatientProfile) {
              openPatientProfile(createdPatient);
            }
            return createdPatient;
          },
          i18next.t('create_loading'),
        );
      } catch (err) {
        const errorDetails = handleSubmitFormError<PatientData>(
          err,
          form.setError,
        );
        if (errorDetails.overallServerError) {
          await modals.showError({ text: errorDetails.overallServerError });
        }
      }
    }),
    setError,
  );
  useEffect(() => {
    if (isExpanded) {
      expandedForm.reset({
        ...expandedForm.getValues(),
        ...miniForm.getValues(),
      });
    } else {
      miniForm.reset({
        ...miniForm.getValues(),
        ...expandedForm.getValues(),
      });
    }
  }, [isExpanded]);

  useEffect(() => {
    if (!isVisibleSubtenants) {
      form.setValue('subtenantId', subtenants[0].id);
    }
  }, [subtenants, isExpanded]);

  const addNewDoctor = useCallback(async () => {
    const createdDoctor = await globalForms.openDoctorCreating(
      DoctorType.External,
    );
    if (createdDoctor) {
      await doctorsQuery.refetch();
      form.setValue('doctorId', createdDoctor.id, validateAndDirty);
    }
  }, [globalForms, form.setValue]);

  const expandedContainerClassName = useMemo(() => {
    return subtenants.length > 1
      ? styles.expandedFormIncreasedContainer
      : styles.expandedFormContainer;
  }, [subtenants]);

  return (
    <form
      className={
        isExpanded ? expandedContainerClassName : styles.miniFormContainer
      }
      onSubmit={handleSubmit(submitForm.handler)}
      data-test-id="createPatientPopup"
    >
      <UpdateContactWhenFieldsChange form={expandedForm} />
      <UpdateContactWhenFieldsChange form={miniForm} />
      <div className={styles.dataContainer}>
        <div
          className={
            isExpanded ? styles.scrollableContainer : 'globalFormContentBody'
          }
        >
          {isExpanded ? (
            <ExpandedPatientForm
              form={expandedForm}
              isSubtenantEnabled={isVisibleSubtenants}
              isDeceasedEnabled={false}
              doctors={doctors}
              addNewDoctor={addNewDoctor}
              subtenants={subtenants}
              onIsValidChange={setIsCustomFieldsValid}
              beforeSaveRef={beforeSaveRef}
              onDoctorsSearch={setDoctorsSearchQuery}
              showCustomFieldsErrors={isCustomFieldsErrorsShown}
              onDirtyChange={setCustomFieldsDirty}
            />
          ) : (
            <PatientMiniForm
              form={miniForm}
              isSubtenantEnabled={isVisibleSubtenants}
              doctors={doctors}
              addNewDoctor={addNewDoctor}
              subtenants={subtenants}
              onDoctorsSearch={setDoctorsSearchQuery}
            />
          )}
        </div>
        <div className={styles.submitContainer}>
          <CheckBox
            checked={isOpenPatientProfile}
            title={i18next.t('open_patient_profile')}
            onChange={(event) =>
              ChangeOpenPatientProfileSettings(event.currentTarget.checked)
            }
            className={styles.openPatientCheckbox}
            name="isOpenPatientProfile"
            testId="createPatient-isOpenPatientProfile"
          />
          {!isExpanded ? (
            <Button
              title={i18next.t('more_options_button')}
              color={ButtonColor.Primary}
              onClick={() => props.expand(formId)}
              className={styles.submit}
              testId={'createPatient-moreOptionsButton'}
            />
          ) : null}
          <SubmitButton
            submitting={formState.isSubmitting}
            className={styles.submit}
            title={i18next.t('create_button')}
            testId={'createPatient-createButton'}
            onClick={() => setCustomFieldErrorsShown(true)}
          />
        </div>
      </div>
    </form>
  );
};
